import { useState, useEffect, useRef, useCallback, useMemo, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from '../../lib/react-leaflet-lite';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  locationSocketService,
  resolveDriverStatus,
  formatElapsed,
} from '../../services/locationSocketService';
import { useAuthToken } from '../../providers/authProviders';
import useAuth from '../../hooks/useAuth';
import { useTheme } from '../../hooks/useTheme';
import { useBasemapTile } from '../../config/basemaps';
import { getDeliveryUsers } from '../../api/user';
import { getFleetDrivers } from '../../api/fleet';
import { getAdminPackages } from '../../api/packages';
import { Search, RefreshCw, WifiOff, History, Navigation, X, Crosshair, Maximize, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from '../../utils/toast';
import {
  decodePolyline6,
  fetchPolylineFromCoords,
  samplePoints,
  createHistoryIcon as createHistoryIconHelper,
  createPendingIcon as createPendingIconHelper,
  createTeardropMarker,
} from '../../utils/mapHelpers';
import { geocodeAddress as geocodeAddressApi } from '../../utils/geocode';
import { Skeleton } from '../../components/ui/Skeleton';
import useOperationFraming from '../../hooks/useOperationFraming';
import InitialFraming from '../../components/map/InitialFraming';
import './FleetMap.scss';

// Fix Leaflet icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Colores de estado leídos de los tokens semánticos (auto dark-mode).
// Se consumen en estilos inline del DOM y en el HTML de los divIcon de
// Leaflet (ambos resuelven var() porque viven en el árbol DOM bajo :root).
const STATUS_COLORS = {
  online: 'rgb(var(--status-success))',
  inactive: 'rgb(var(--status-warning))',
  offline: 'rgb(var(--status-neutral))',
};
const STATUS_LABELS = { online: 'EN LINEA', inactive: 'INACTIVO', offline: 'OFFLINE' };
const STATUS_BG = {
  online: 'rgb(var(--status-success) / 0.12)',
  inactive: 'rgb(var(--status-warning) / 0.12)',
  offline: 'rgb(var(--status-neutral) / 0.12)',
};

// Intervalo de flush del snapshot hacia panel y stats (ms)
const SNAPSHOT_INTERVAL_MS = 10_000;


// ─────────────────────────────────────────────────────────────
// DriverRow — memoizado: solo re-renderiza cuando cambian sus
// propios datos (no cuando otro driver se mueve en el mapa).
// ─────────────────────────────────────────────────────────────
const DriverRow = memo(function DriverRow({
  driverId,
  location,
  driverName,
  status,
  isSelected,
  onSelect,
}) {
  const { t } = useTranslation('tracking');
  const initials = driverName.substring(0, 2).toUpperCase();
  return (
    <div
      className={`fleet-panel__driver ${isSelected ? 'fleet-panel__driver--selected' : ''}`}
      onClick={() => onSelect(driverId)}
    >
      <div className="fleet-panel__avatar">
        <span
          className="fleet-panel__avatar-circle"
          style={{ background: STATUS_COLORS[status] }}
        >
          {initials}
        </span>
        <span className="fleet-panel__avatar-dot" style={{ background: STATUS_COLORS[status] }} />
      </div>

      <div className="fleet-panel__driver-info">
        <div className="fleet-panel__driver-top">
          <span className="fleet-panel__driver-name">{driverName}</span>
          <span
            className="fleet-panel__status-badge"
            style={{ color: STATUS_COLORS[status], background: STATUS_BG[status] }}
          >
            {t(STATUS_LABELS[status])}
          </span>
        </div>
        <div className="fleet-panel__driver-bottom">
          {location?.speed != null ? (
            <span className="fleet-panel__driver-meta">
              {(location.speed * 3.6).toFixed(0)} km/h
            </span>
          ) : (
            <span className="fleet-panel__driver-meta">—</span>
          )}
          <span className="fleet-panel__driver-time">{formatElapsed(location?.timestamp)}</span>
        </div>
      </div>

      <Crosshair size={14} className="fleet-panel__locate-icon" />
    </div>
  );
}, (prev, next) =>
  prev.driverId === next.driverId &&
  prev.isSelected === next.isSelected &&
  prev.status === next.status &&
  prev.driverName === next.driverName &&
  prev.location?.timestamp === next.location?.timestamp
);

// ─────────────────────────────────────────────────────────────
// MapUpdater — centra y sigue al driver seleccionado en el mapa.
//
// Estrategia para no pisarse con el usuario:
//  - isProgrammaticRef: se pone true ANTES de cada setView/panTo
//    programático y se limpia en el evento 'moveend'. Así el listener
//    de 'dragstart' sabe si el movimiento lo inició el usuario o el código.
//  - Cuando el usuario arrastra (dragstart con isProgrammatic=false)
//    llamamos onStopTracking para deshabilitar el seguimiento automático.
//  - El zoom manual NO detiene el tracking: el usuario puede hacer
//    zoom-in/zoom-out sin perder el seguimiento; en el próximo update
//    el mapa solo hace panTo (respeta el zoom actual).
// ─────────────────────────────────────────────────────────────
function MapUpdater({ selectedDriver, markerLocations, isTracking, onStopTracking }) {
  const map = useMap();
  const lastDriverRef      = useRef(null);
  const focusedRef         = useRef(false);
  const isProgrammaticRef  = useRef(false);

  // Detectar drag iniciado por el usuario → pausar tracking
  useEffect(() => {
    if (!map) return;
    const handleDragStart = () => {
      if (!isProgrammaticRef.current) {
        onStopTracking?.();
      }
    };
    map.on('dragstart', handleDragStart);
    return () => { map.off('dragstart', handleDragStart); };
  }, [map, onStopTracking]);

  // Resetear estado de "ya enfocado" al cambiar de driver
  useEffect(() => {
    if (lastDriverRef.current !== selectedDriver) {
      focusedRef.current = false;
      lastDriverRef.current = selectedDriver;
    }
  }, [selectedDriver]);

  useEffect(() => {
    if (!selectedDriver || !markerLocations.has(selectedDriver) || !map) return;
    const loc = markerLocations.get(selectedDriver);
    const pos = [loc.latitude, loc.longitude];
    try {
      if (isTracking) {
        isProgrammaticRef.current = true;
        if (!focusedRef.current) {
          // Primera vez: centrar con zoom fijo
          map.setView(pos, 16, { animate: true, duration: 0.5 });
          focusedRef.current = true;
        } else {
          // Actualizaciones posteriores: solo mover el centro, respetar zoom del usuario
          map.panTo(pos, { animate: true, duration: 0.5 });
        }
        // Limpiar la bandera cuando termine la animación
        map.once('moveend', () => { isProgrammaticRef.current = false; });
      } else {
        // Sin tracking activo: centrar una sola vez al seleccionar el driver
        if (!focusedRef.current) {
          isProgrammaticRef.current = true;
          map.setView(pos, 16, { animate: true, duration: 0.4 });
          focusedRef.current = true;
          map.once('moveend', () => { isProgrammaticRef.current = false; });
        }
      }
    } catch {}
  }, [selectedDriver, markerLocations, map, isTracking]);

  return null;
}

// ─────────────────────────────────────────────────────────────
// MapZoomControls — botones +/- usando la API interna de Leaflet.
// El botón "Encuadrar todos" ajusta el viewport a toda la flota
// visible reutilizando fitBounds (misma API que MapFitBounds).
// ─────────────────────────────────────────────────────────────
function MapZoomControls({ fitPositions }) {
  const map = useMap();
  const { t } = useTranslation('tracking');
  const hasPositions = Array.isArray(fitPositions) && fitPositions.length > 0;

  const handleFitAll = () => {
    if (!hasPositions) return;
    try {
      if (fitPositions.length === 1) {
        map.setView(fitPositions[0], 15, { animate: true });
      } else {
        map.fitBounds(L.latLngBounds(fitPositions), { padding: [60, 60], animate: true });
      }
    } catch {}
  };

  return (
    <div className="fleet-controls">
      <button className="fleet-controls__btn fleet-controls__btn--zoom" onClick={() => map.zoomIn()} title={t('Acercar')}>+</button>
      <button className="fleet-controls__btn fleet-controls__btn--zoom" onClick={() => map.zoomOut()} title={t('Alejar')}>−</button>
      <button
        className="fleet-controls__btn fleet-controls__btn--fit"
        onClick={handleFitAll}
        disabled={!hasPositions}
        title={t('Encuadrar todos')}
        aria-label={t('Encuadrar todos')}
      >
        <Maximize size={16} />
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// MapFitBounds — ajusta el viewport al extent de la polyline
// cuando se carga el recorrido road-snapped.
// ─────────────────────────────────────────────────────────────
function MapFitBounds({ positions }) {
  const map = useMap();
  useEffect(() => {
    if (!positions || positions.length < 2) return;
    try {
      map.fitBounds(L.latLngBounds(positions), { padding: [40, 40], animate: true });
    } catch {}
  // Solo re-corre cuando cambia la referencia de positions (nueva carga)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positions]);
  return null;
}

// ─────────────────────────────────────────────────────────────
// FleetMap — componente principal
// ─────────────────────────────────────────────────────────────
export default function FleetMap() {
  const { t } = useTranslation('tracking');
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthToken();
  const { user: authUser } = useAuth();
  const { isDark } = useTheme();
  // Tile del basemap activo (fuente única configurable desde Configuración).
  const tile = useBasemapTile(isDark);

  const historyIntervalRef = useRef(null);
  const driverNamesRef = useRef(new Map());

  // ── Capa 1: refs que se actualizan con cada evento WS (sin re-render) ──
  const locationsRef   = useRef(new Map()); // ubicaciones crudas, tiempo real
  const onlineIdsRef   = useRef(new Set()); // IDs con sesión WS activa
  const sortedIdsRef   = useRef([]);        // orden estable de la lista
  const lastNamesSizeRef = useRef(0);       // detecta carga de nombres para re-sort

  // ── Capa 1 state: posiciones de marcadores en el mapa (actualiza inmediatamente) ──
  const [markerLocations, setMarkerLocations] = useState(new Map());

  // ── Capa 2 state: snapshot que alimenta panel + stats (flush cada 10s) ──
  const [snapshotLocations, setSnapshotLocations] = useState(new Map());
  const [snapshotOnlineIds, setSnapshotOnlineIds] = useState(new Set());

  // UI state
  const [isConnected, setIsConnected]       = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [driverNames, setDriverNames]       = useState(new Map());
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [isTracking, setIsTracking]         = useState(false);
  const [searchQuery, setSearchQuery]       = useState('');
  const [statusFilter, setStatusFilter]     = useState('all');
  // Hoja inferior (mobile): colapsada deja visible solo handle + header
  const [sheetCollapsed, setSheetCollapsed] = useState(false);

  // Pending packages
  const [pendingPoints, setPendingPoints] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [showPending, setShowPending] = useState(false);

  // History / replay
  const [historyPoints, setHistoryPoints] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [replayIndex, setReplayIndex] = useState(0);
  const [isReplaying, setIsReplaying] = useState(false);
  // routePolyline: polyline road-snapped del optimizer (formato [[lat,lng]...])
  // Se usa cuando está disponible; si falla, se cae al historyPolyline crudo.
  const [routePolyline, setRoutePolyline] = useState([]);

  // Encuadre de arranque del <MapContainer>. Es provisional por diseño: el mapa se
  // crea una sola vez y `center`/`zoom` son inertes después (lib/react-leaflet-lite.jsx),
  // así que el encuadre real lo aplica <InitialFraming> cuando sabe dónde opera el
  // cliente. Se mantiene un valor concreto porque L.map() exige uno para inicializar.
  const defaultCenter = useMemo(() => [-33.4489, -70.6693], []);

  // Dónde opera el cliente (instalaciones de /admin/settings?tab=locations), para
  // arrancar el mapa ahí cuando no hay ningún repartidor con GPS que mostrar.
  const operationFrame = useOperationFraming();

  // ────────────────────────────────────────────────────────────
  // Flush de snapshot cada 10s (Capa 2)
  // Panel, stats y DriverRow leen de snapshotLocations,
  // que solo se actualiza aquí → sin parpadeo en la lista.
  // ────────────────────────────────────────────────────────────
  useEffect(() => {
    const id = setInterval(() => {
      setSnapshotLocations(new Map(locationsRef.current));
      setSnapshotOnlineIds(new Set(onlineIdsRef.current));
    }, SNAPSHOT_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  // ────────────────────────────────────────────────────────────
  // Helper interno: poblar refs + ambos estados a la vez.
  // Usado en carga inicial y refresh manual.
  // ────────────────────────────────────────────────────────────
  const applyFullRefresh = useCallback((locations) => {
    const names = driverNamesRef.current;
    const newMap = new Map();
    const newSet = new Set();
    locations.forEach(({ driverId, location }) => {
      if (!driverId || !location) return;
      if (names.size > 0 && !names.has(driverId)) return;
      newMap.set(driverId, location);
      if (location.status === 'online') newSet.add(driverId);
    });
    locationsRef.current   = newMap;
    onlineIdsRef.current   = newSet;
    setMarkerLocations(new Map(newMap));
    setSnapshotLocations(new Map(newMap));
    setSnapshotOnlineIds(new Set(newSet));
  }, []);

  // ────────────────────────────────────────────────────────────
  // Cargar nombres de drivers (una sola vez al montar)
  // ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated || !authUser) return;
    (async () => {
      try {
        const isCommerceFleet = authUser?.company?.hasOwnFleet && !authUser?.logisticCompanyId;

        let users = [];
        if (isCommerceFleet) {
          const result = await getFleetDrivers();
          users = result?.data || [];
        } else {
          const logisticCompanyId = authUser?.logisticCompanyId || authUser?.companyId;
          const result = await getDeliveryUsers('active', 'delivery', logisticCompanyId);
          users = result?.docs || result?.data?.docs || (Array.isArray(result) ? result : []);
        }

        if (Array.isArray(users)) {
          const namesMap = new Map();
          users.forEach((u) => {
            if (u._id) {
              const fullName = u.name
                ? `${u.name}${u.lastName ? ` ${u.lastName}` : ''}`.trim()
                : u._id;
              namesMap.set(u._id, fullName);
            }
          });
          driverNamesRef.current = namesMap;
          setDriverNames(namesMap);
        }
      } catch {}
    })();
  }, [isAuthenticated, authUser]);

  // Re-filtrar locations cuando driverNames se carga (puede llegar después del REST inicial)
  useEffect(() => {
    if (driverNames.size === 0) return;
    const filtered = new Map();
    const filteredOnline = new Set();
    locationsRef.current.forEach((loc, id) => {
      if (driverNames.has(id)) {
        filtered.set(id, loc);
        if (loc.status === 'online') filteredOnline.add(id);
      }
    });
    locationsRef.current = filtered;
    onlineIdsRef.current = filteredOnline;
    setMarkerLocations(new Map(filtered));
    setSnapshotLocations(new Map(filtered));
    setSnapshotOnlineIds(new Set(filteredOnline));
  }, [driverNames]);

  // ────────────────────────────────────────────────────────────
  // Carga inicial via REST + subscripción WebSocket
  // ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const adminId = localStorage.getItem('adminId') || 'admin';

    // Carga REST inicial → puebla ambas capas inmediatamente
    (async () => {
      try {
        const locations = await locationSocketService.fetchLocationsREST();
        if (Array.isArray(locations) && locations.length > 0) {
          applyFullRefresh(locations);
        }
      } catch (err) {
        console.warn('[FleetMap] REST carga inicial fallida:', err.message);
      } finally {
        setIsInitialLoading(false);
      }
    })();

    locationSocketService.connect(adminId);

    // Reconexión WS → refrescar snapshot desde REST
    const unsubConn = locationSocketService.onConnectionChange((connected) => {
      setIsConnected(connected);
      if (!connected) {
        setTimeout(async () => {
          try {
            const locations = await locationSocketService.fetchLocationsREST();
            if (Array.isArray(locations)) applyFullRefresh(locations);
          } catch {}
        }, 2000);
      }
    });

    // Capa 1: cada update WS escribe en ref + actualiza marcadores inmediatamente
    const unsubLoc = locationSocketService.onLocationUpdate(({ driverId, location }) => {
      if (!driverId || !location) return;
      const names = driverNamesRef.current;
      if (names.size > 0 && !names.has(driverId)) return;
      locationsRef.current.set(driverId, location);
      if (location.status === 'online') onlineIdsRef.current.add(driverId);
      // Solo actualizar marcadores (mapa) — no panel ni stats
      setMarkerLocations((prev) => {
        const next = new Map(prev);
        next.set(driverId, location);
        return next;
      });
    });

    // Capa 1: cambios de estado WS escriben en ref (panel actualiza al próximo flush)
    const unsubStatus = locationSocketService.onDriverStatusChange((driverId, status) => {
      if (status === 'online') onlineIdsRef.current.add(driverId);
      else onlineIdsRef.current.delete(driverId);
    });

    return () => {
      if (historyIntervalRef.current) clearInterval(historyIntervalRef.current);
      unsubConn();
      unsubLoc();
      unsubStatus();
      locationSocketService.disconnect();
    };
  }, [isAuthenticated, navigate, applyFullRefresh]);

  // ────────────────────────────────────────────────────────────
  // Refresh manual — actualiza ambas capas de inmediato
  // ────────────────────────────────────────────────────────────
  const handleRefresh = useCallback(async () => {
    setIsInitialLoading(true);
    try {
      const locations = await locationSocketService.fetchLocationsREST();
      if (Array.isArray(locations)) {
        applyFullRefresh(locations);
        toast.success(t('{{n}} ubicaciones actualizadas', { n: locations.length }));
      }
    } catch {
      toast.error(t('Error al actualizar ubicaciones'));
    } finally {
      setIsInitialLoading(false);
    }
  }, [applyFullRefresh]);

  // ────────────────────────────────────────────────────────────
  // Selección de driver
  // ────────────────────────────────────────────────────────────
  const handleDriverSelect = useCallback((driverId) => {
    setSelectedDriver((prev) => {
      if (prev === driverId) {
        setIsTracking(false);
        setShowHistory(false);
        setHistoryPoints([]);
        setRoutePolyline([]);
        setPendingPoints([]);
        setShowPending(false);
        return null;
      }
      setIsTracking(true);
      setShowHistory(false);
      setHistoryPoints([]);
      setRoutePolyline([]);
      setPendingPoints([]);
      setShowPending(false);
      return driverId;
    });
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelectedDriver(null);
    setIsTracking(false);
    setShowHistory(false);
    setHistoryPoints([]);
    setRoutePolyline([]);
    setPendingPoints([]);
    setShowPending(false);
    setIsReplaying(false);
  }, []);

  // ────────────────────────────────────────────────────────────
  // Historial de recorrido / replay
  // ────────────────────────────────────────────────────────────
  const handleLoadHistory = useCallback(async () => {
    if (!selectedDriver) return;
    setHistoryLoading(true);
    setShowHistory(false);
    setHistoryPoints([]);
    setRoutePolyline([]);
    setIsReplaying(false);
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const data = await locationSocketService.fetchDriverHistory(selectedDriver, today, new Date(), 2000);
      if (!data?.points?.length) {
        toast.info(t('Sin recorrido registrado hoy para este driver'));
        return;
      }

      const points = data.points;
      setHistoryPoints(points);
      setReplayIndex(0);

      // Intentar obtener polyline road-snapped del optimizer
      try {
        const sampled = samplePoints(points, 100);
        const result  = await fetchPolylineFromCoords(sampled);
        if (result?.polyline) {
          const decoded = decodePolyline6(result.polyline);
          setRoutePolyline(decoded);
          toast.success(t('Recorrido cargado · {{n}} pts · {{km}} km', { n: points.length, km: (result.totals?.distance_m / 1000).toFixed(1) }));
        } else {
          setRoutePolyline([]);
          toast.success(t('{{n}} puntos de recorrido cargados', { n: points.length }));
        }
      } catch (optimizerErr) {
        // Fallback silencioso: se usará la polyline cruda (linea recta entre GPS)
        console.warn('[FleetMap] Optimizer fallback:', optimizerErr.message);
        setRoutePolyline([]);
        toast.success(t('{{n}} puntos de recorrido cargados', { n: points.length }));
      }

      setShowHistory(true);
    } catch {
      toast.error(t('Error al cargar historial de recorrido'));
    } finally {
      setHistoryLoading(false);
    }
  }, [selectedDriver]);

  const handleStartReplay = useCallback(() => {
    if (historyPoints.length === 0) return;
    setIsReplaying(true);
    setReplayIndex(0);
    historyIntervalRef.current = setInterval(() => {
      setReplayIndex((prev) => {
        if (prev >= historyPoints.length - 1) {
          clearInterval(historyIntervalRef.current);
          setIsReplaying(false);
          return prev;
        }
        return prev + 1;
      });
    }, 200);
  }, [historyPoints]);

  const handleStopReplay = useCallback(() => {
    clearInterval(historyIntervalRef.current);
    setIsReplaying(false);
  }, []);

  // ────────────────────────────────────────────────────────────
  // Paquetes pendientes
  // ────────────────────────────────────────────────────────────
  // Geocodifica vía el servicio compartido (addresses-api.beartrackapp.com con el
  // JWT del usuario). Se conserva el contrato local { lat, lng, display } + throw
  // ante fallo para no tocar el flujo de Promise.allSettled de abajo.
  const geocodeAddress = useCallback(async (query) => {
    const r = await geocodeAddressApi(query);
    if (!r) throw new Error('Sin resultados');
    return { lat: r.lat, lng: r.lng, display: r.label };
  }, []);

  const fetchPendingPoints = useCallback(async () => {
    if (!selectedDriver) return;
    setPendingLoading(true);
    setShowPending(true);
    setPendingPoints([]);
    try {
      const todayISO = new Date().toISOString().split('T')[0];
      const resp = await getAdminPackages({
        page: 1, limit: 100, orderField: 'updatedAt', sortType: 'desc',
        assignTo: selectedDriver, status: ['3', '30'], updatedAtSince: todayISO,
      });
      const docs = resp?.data?.docs || resp?.data?.data?.docs || [];
      if (!Array.isArray(docs) || docs.length === 0) {
        toast.info(t('No hay paquetes pendientes hoy'));
        return;
      }
      const targets = docs
        .map((pkg) => {
          const addr = pkg?.shipping_address || {};
          const mun  = addr?.municipality?.name;
          if (!mun) return null;
          const query = addr.street_name && addr.street_number
            ? `${addr.street_name} ${addr.street_number}, ${mun}`
            : addr.address_line ? `${addr.address_line}, ${mun}` : null;
          return query ? { id: pkg._id, query, pkg } : null;
        })
        .filter(Boolean)
        .slice(0, 100);

      const results = await Promise.allSettled(
        targets.map(async ({ id, query, pkg }) => {
          const geo = await geocodeAddress(query);
          return { id, lat: geo.lat, lng: geo.lng, display: geo.display, query, status: pkg.status, followingNumber: pkg.followingNumber };
        }),
      );
      const successful = results.filter((r) => r.status === 'fulfilled').map((r) => r.value);
      setPendingPoints(successful);
      if (successful.length === 0) toast.warn(t('No se pudo geolocalizar ninguna dirección'));
    } catch {
      toast.error(t('Error al cargar los puntos pendientes'));
    } finally {
      setPendingLoading(false);
    }
  }, [selectedDriver, geocodeAddress]);

  // ────────────────────────────────────────────────────────────
  // Iconos de marcadores
  // ────────────────────────────────────────────────────────────
  // Migrado a createTeardropMarker (audit #8). El borde es siempre 3px para
  // mantener la apariencia previa (selected y no-selected compartían 3px).
  const createDriverIcon = useCallback((driverId, isSelected, driverName, status) => {
    const text  = driverName ? driverName.substring(0, 2).toUpperCase() : driverId.substring(0, 2).toUpperCase();
    const color = isSelected ? 'rgb(var(--status-error))' : STATUS_COLORS[status] || STATUS_COLORS.offline;
    const size  = isSelected ? 38 : 32;
    return createTeardropMarker({
      size,
      color,
      borderColor: 'white',
      borderWidth: 3,
      content: text,
      contentColor: 'white',
      contentSize: isSelected ? 12 : 10,
      shadow: '0 2px 6px rgba(0,0,0,0.35)',
      className: 'custom-driver-marker',
    });
  }, []);

  const createPendingIcon = useCallback(() => createPendingIconHelper(), []);

  const createHistoryIcon = useCallback((isHead) => createHistoryIconHelper(isHead), []);

  // ────────────────────────────────────────────────────────────
  // Lista estable de drivers (Capa 2)
  // El orden solo se recalcula cuando cambia la composición de
  // drivers (alguien entra/sale) o cuando cargan los nombres.
  // Las actualizaciones de velocidad/timestamp no lo alteran.
  // ────────────────────────────────────────────────────────────
  const driversList = useMemo(() => {
    const currentIds = new Set(snapshotLocations.keys());
    const prevSet    = new Set(sortedIdsRef.current);

    const hasStructuralChange =
      currentIds.size !== prevSet.size ||
      driverNames.size !== lastNamesSizeRef.current ||
      [...currentIds].some((id) => !prevSet.has(id));

    if (hasStructuralChange) {
      lastNamesSizeRef.current = driverNames.size;
      const idsToSort = driverNames.size > 0
        ? [...currentIds].filter((id) => driverNames.has(id))
        : [...currentIds];
      sortedIdsRef.current = idsToSort.sort((a, b) =>
        (driverNames.get(a) || a).localeCompare(driverNames.get(b) || b),
      );
    }

    let ids = sortedIdsRef.current;

    // Si hay un driver seleccionado, anclarlo al tope de la lista visible
    if (selectedDriver && ids.includes(selectedDriver)) {
      ids = [selectedDriver, ...ids.filter((id) => id !== selectedDriver)];
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      ids = ids.filter((id) => {
        const name = driverNames.get(id) || '';
        return name.toLowerCase().includes(q) || id.toLowerCase().includes(q);
      });
    }

    if (statusFilter !== 'all') {
      ids = ids.filter((id) => {
        const loc    = snapshotLocations.get(id);
        const status = resolveDriverStatus(loc ? { ...loc, driverId: id } : null, snapshotOnlineIds);
        return status === statusFilter;
      });
    }

    return ids;
  }, [snapshotLocations, driverNames, searchQuery, statusFilter, snapshotOnlineIds, selectedDriver]);

  const getDriverName = useCallback((id) => driverNames.get(id) || id, [driverNames]);

  // getDriverStatus: usa snapshot para panel/footer (estable)
  const getDriverStatus = useCallback((id) => {
    const loc = snapshotLocations.get(id);
    return resolveDriverStatus(loc ? { ...loc, driverId: id } : null, snapshotOnlineIds);
  }, [snapshotLocations, snapshotOnlineIds]);

  // getMarkerStatus: usa datos en tiempo real para los iconos del mapa
  const getMarkerStatus = useCallback((id) => {
    const loc = markerLocations.get(id);
    return resolveDriverStatus(loc ? { ...loc, driverId: id } : null, snapshotOnlineIds);
  }, [markerLocations, snapshotOnlineIds]);

  // Posiciones visibles en el mapa (tiempo real) para "Encuadrar todos".
  // Respeta el mismo filtrado que los marcadores: nombres conocidos y
  // el statusFilter activo. Devuelve [[lat,lng]...] para fitBounds.
  const fitAllPositions = useMemo(() => {
    const pts = [];
    markerLocations.forEach((location, driverId) => {
      if (driverNames.size > 0 && !driverNames.has(driverId)) return;
      if (statusFilter !== 'all' && getMarkerStatus(driverId) !== statusFilter) return;
      if (location?.latitude != null && location?.longitude != null) {
        pts.push([location.latitude, location.longitude]);
      }
    });
    return pts;
  }, [markerLocations, driverNames, statusFilter, getMarkerStatus]);

  // Puntos con los que se puede encuadrar el mapa al abrir la vista.
  //
  // Se exige que `driverNames` ya haya cargado: hasta entonces `applyFullRefresh`
  // deja pasar TODAS las ubicaciones del REST sin filtrar (ver el `names.size > 0`
  // de esa función), así que encuadrar con el primer payload podría abarcar
  // repartidores que la vista va a ocultar un segundo después —y el encuadre
  // inicial es de una sola vez, así que ese error no se corregiría solo—. Con la
  // lista vacía el mapa cae al respaldo de instalaciones, que es lo correcto:
  // si todavía no se sabe quiénes son los repartidores propios, no hay que
  // encuadrar sobre posiciones que pueden no serlo.
  const initialFramePositions = useMemo(
    () => (driverNames.size > 0 ? fitAllPositions : []),
    [driverNames, fitAllPositions],
  );

  // Stats — leen del snapshot para estabilidad
  const stats = useMemo(() => {
    let online = 0, inactive = 0, offline = 0;
    snapshotLocations.forEach((loc, id) => {
      const s = resolveDriverStatus({ ...loc, driverId: id }, snapshotOnlineIds);
      if (s === 'online') online++;
      else if (s === 'inactive') inactive++;
      else offline++;
    });
    return { online, inactive, offline, total: snapshotLocations.size };
  }, [snapshotLocations, snapshotOnlineIds]);

  // Polyline cruda (puntos GPS sin ajuste a calles) — usada para replay y fallback
  const historyPolyline = useMemo(
    () => historyPoints.map((p) => [p.latitude, p.longitude]),
    [historyPoints],
  );
  // displayPolyline: road-snapped si el optimizer respondió, cruda en caso contrario
  const displayPolyline = routePolyline.length > 1 ? routePolyline : historyPolyline;

  const replayPolyline = useMemo(
    () => historyPolyline.slice(0, replayIndex + 1),
    [historyPolyline, replayIndex],
  );
  const replayPosition = historyPoints[replayIndex];

  // ────────────────────────────────────────────────────────────
  // Render
  // ────────────────────────────────────────────────────────────
  return (
    <div className={`fleet-map ${sheetCollapsed ? 'fleet-map--sheet-collapsed' : ''}`}>

      {/* ── Mapa: ocupa el fondo completo ── */}
      <MapContainer
        center={defaultCenter}
        zoom={12}
        style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, height: '100%', width: '100%', zIndex: 1 }}
        zoomControl={false}
      >
        <TileLayer
          key={tile.url}
          url={tile.url}
          attribution={tile.attribution}
          subdomains={tile.subdomains}
          maxZoom={tile.maxZoom}
        />
        {/* Usa markerLocations (tiempo real) para seguimiento fluido */}
        <MapUpdater
          selectedDriver={selectedDriver}
          markerLocations={markerLocations}
          isTracking={isTracking && !showHistory}
          onStopTracking={() => setIsTracking(false)}
        />
        <MapZoomControls fitPositions={fitAllPositions} />

        {/* Encuadre de apertura. Cede el mapa en cuanto otro controlador toma la
            cámara: MapUpdater al seleccionar un repartidor, MapFitBounds al abrir
            un recorrido. Sin eso serían dos autoridades sobre el mismo L.Map. */}
        <InitialFraming
          frame={operationFrame}
          dataPoints={initialFramePositions}
          enabled={!selectedDriver && !showHistory}
          obstructionSelector=".fleet-panel"
          maxZoom={15}
          singlePointZoom={15}
        />

        {/* Ajusta el viewport al recorrido road-snapped al cargarlo */}
        {showHistory && !isReplaying && (
          <MapFitBounds positions={displayPolyline} />
        )}

        {/* Marcadores de drivers — posición en tiempo real (markerLocations).
            Cuando hay un recorrido activo se ocultan los demás drivers para
            centrar la atención en el recorrido del driver seleccionado. */}
        {Array.from(markerLocations.entries())
          .filter(([driverId]) => driverNames.size === 0 || driverNames.has(driverId))
          .filter(([driverId]) => !showHistory || driverId === selectedDriver)
          .filter(([driverId]) => statusFilter === 'all' || getMarkerStatus(driverId) === statusFilter)
          .map(([driverId, location]) => {
            const isSelected  = selectedDriver === driverId;
            const status      = getMarkerStatus(driverId);
            const driverName  = getDriverName(driverId);
            return (
              <Marker
                key={driverId}
                position={[location.latitude, location.longitude]}
                icon={createDriverIcon(driverId, isSelected, driverName, status)}
                eventHandlers={{ click: () => handleDriverSelect(driverId) }}
              >
                <Popup>
                  <div style={{ padding: 8, minWidth: 180 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                      <span style={{ width: 10, height: 10, borderRadius: '50%', background: STATUS_COLORS[status], display: 'inline-block' }} />
                      <strong style={{ fontSize: 14 }}>{driverName}</strong>
                    </div>
                    <p style={{ margin: '3px 0', fontSize: 12, color: 'var(--text-secondary)' }}>
                      {t('Estado')}: <strong style={{ color: STATUS_COLORS[status] }}>{t(STATUS_LABELS[status])}</strong>
                    </p>
                    <p style={{ margin: '3px 0', fontSize: 12 }}>Lat: {location.latitude.toFixed(6)}</p>
                    <p style={{ margin: '3px 0', fontSize: 12 }}>Lng: {location.longitude.toFixed(6)}</p>
                    {location.speed != null && (
                      <p style={{ margin: '3px 0', fontSize: 12 }}>{t('Velocidad')}: {(location.speed * 3.6).toFixed(1)} km/h</p>
                    )}
                    {location.accuracy != null && (
                      <p style={{ margin: '3px 0', fontSize: 12 }}>{t('Precisión')}: ±{location.accuracy.toFixed(0)} m</p>
                    )}
                    <p style={{ margin: '3px 0', fontSize: 12, color: 'var(--text-tertiary)' }}>{formatElapsed(location.timestamp)}</p>
                  </div>
                </Popup>
              </Marker>
            );
          })}

        {/* Historial de recorrido — polyline road-snapped (o cruda como fallback) */}
        {showHistory && displayPolyline.length > 1 && (
          <>
            <Polyline
              positions={isReplaying ? replayPolyline : displayPolyline}
              color="#1d4ed8"
              weight={5}
              opacity={1}
            />
            {!isReplaying && historyPoints.length > 0 && (
              <Marker
                position={[historyPoints[historyPoints.length - 1].latitude, historyPoints[historyPoints.length - 1].longitude]}
                icon={createHistoryIcon(true)}
              >
                <Popup>
                  <div style={{ padding: 8, fontSize: 12 }}>
                    {t('Ultimo punto')}<br />
                    {new Date(historyPoints[historyPoints.length - 1].timestamp).toLocaleTimeString()}
                  </div>
                </Popup>
              </Marker>
            )}
            {isReplaying && replayPosition && (
              <Marker
                position={[replayPosition.latitude, replayPosition.longitude]}
                icon={createHistoryIcon(true)}
              >
                <Popup>
                  <div style={{ padding: 8, fontSize: 12 }}>
                    {new Date(replayPosition.timestamp).toLocaleTimeString()}
                  </div>
                </Popup>
              </Marker>
            )}
          </>
        )}

        {/* Paquetes pendientes */}
        {showPending && pendingPoints.map((point) => (
          <Marker key={`pending-${point.id}`} position={[point.lat, point.lng]} icon={createPendingIcon()}>
            <Popup>
              <div style={{ padding: 8, maxWidth: 220, fontSize: 12 }}>
                <strong>{t('Paquete pendiente')}</strong>
                {point.followingNumber && <p style={{ margin: '4px 0' }}>Tracking: {point.followingNumber}</p>}
                <p style={{ margin: '4px 0', color: 'var(--text-secondary)' }}>{point.display || point.query}</p>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>

      {/* ── Stats HUD: top-left — se actualiza cada 10s ── */}
      <div className="fleet-hud">
        <button
          className={`fleet-hud__card ${statusFilter === 'all' ? 'fleet-hud__card--active' : ''}`}
          onClick={() => setStatusFilter('all')}
        >
          <p className="fleet-hud__label">{t('Total drivers')}</p>
          <div className="fleet-hud__row">
            <span className="fleet-hud__value">{stats.total}</span>
            <span className="fleet-hud__sub">{t('Registrados')}</span>
          </div>
        </button>

        <button
          className={`fleet-hud__card fleet-hud__card--online ${statusFilter === 'online' ? 'fleet-hud__card--active' : ''}`}
          onClick={() => setStatusFilter(statusFilter === 'online' ? 'all' : 'online')}
        >
          <p className="fleet-hud__label" style={{ color: 'rgb(var(--status-success))' }}>{t('En linea')}</p>
          <div className="fleet-hud__row">
            <span className="fleet-hud__value">{stats.online}</span>
            <span className="fleet-hud__icon fleet-hud__icon--online">●</span>
          </div>
        </button>

        <button
          className={`fleet-hud__card fleet-hud__card--inactive ${statusFilter === 'inactive' ? 'fleet-hud__card--active' : ''}`}
          onClick={() => setStatusFilter(statusFilter === 'inactive' ? 'all' : 'inactive')}
        >
          <p className="fleet-hud__label" style={{ color: 'rgb(var(--status-warning))' }}>{t('Inactivos')}</p>
          <div className="fleet-hud__row">
            <span className="fleet-hud__value">{stats.inactive}</span>
            <span className="fleet-hud__icon fleet-hud__icon--inactive">●</span>
          </div>
        </button>

        <button
          className={`fleet-hud__card fleet-hud__card--offline ${statusFilter === 'offline' ? 'fleet-hud__card--active' : ''}`}
          onClick={() => setStatusFilter(statusFilter === 'offline' ? 'all' : 'offline')}
        >
          <p className="fleet-hud__label" style={{ color: 'rgb(var(--status-neutral))' }}>{t('Offline')}</p>
          <div className="fleet-hud__row">
            <span className="fleet-hud__value">{stats.offline}</span>
            <span className="fleet-hud__icon fleet-hud__icon--offline">●</span>
          </div>
        </button>
      </div>

      {/* ── Panel derecho: lista de drivers ── */}
      <div className={`fleet-panel ${sheetCollapsed ? 'fleet-panel--collapsed' : ''}`}>
        <button
          type="button"
          className="fleet-panel__handle"
          onClick={() => setSheetCollapsed((v) => !v)}
          aria-expanded={!sheetCollapsed}
          aria-label={sheetCollapsed ? t('Expandir panel') : t('Colapsar panel')}
        >
          {sheetCollapsed ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </button>
        <div className="fleet-panel__header">
          <div className="fleet-panel__header-info">
            <h3 className="fleet-panel__title">{t('Estado de drivers')}</h3>
            <p className="fleet-panel__subtitle">
              {isConnected ? (
                <><span className="fleet-panel__live-dot" />{t('Telemetria en tiempo real')}</>
              ) : (
                <><WifiOff size={10} style={{ marginRight: 4 }} />{t('Sin conexion en vivo')}</>
              )}
            </p>
          </div>
          <button
            className="fleet-panel__refresh"
            onClick={handleRefresh}
            disabled={isInitialLoading}
            title={t('Actualizar')}
          >
            <RefreshCw size={14} className={isInitialLoading ? 'fleet-panel__refresh--spinning' : ''} />
          </button>
        </div>

        <div className="fleet-panel__search">
          <Search size={14} className="fleet-panel__search-icon" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('Buscar driver...')}
            className="fleet-panel__search-input"
          />
        </div>

        {/* Lista — cada DriverRow es React.memo: no re-renderiza
            cuando el mapa actualiza marcadores (markerLocations) */}
        <div className="fleet-panel__list">
          {isInitialLoading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="fleet-panel__skel" />
            ))
          ) : driversList.length === 0 ? (
            <p className="fleet-panel__empty">
              {searchQuery.trim() || statusFilter !== 'all'
                ? t('Sin resultados')
                : t('Sin drivers con ubicacion registrada')}
            </p>
          ) : (
            driversList.map((driverId) => (
              <DriverRow
                key={driverId}
                driverId={driverId}
                location={snapshotLocations.get(driverId)}
                driverName={getDriverName(driverId)}
                status={getDriverStatus(driverId)}
                isSelected={selectedDriver === driverId}
                onSelect={handleDriverSelect}
              />
            ))
          )}
        </div>

        {/* Footer: acciones del driver seleccionado */}
        {selectedDriver && (
          <div className="fleet-panel__footer">
            <div className="fleet-panel__footer-header">
              <div className="fleet-panel__footer-name">
                <span
                  className="fleet-panel__footer-dot"
                  style={{ background: STATUS_COLORS[getDriverStatus(selectedDriver)] }}
                />
                {getDriverName(selectedDriver)}
              </div>
              <button className="fleet-panel__footer-close" onClick={handleClearSelection}>
                <X size={14} />
              </button>
            </div>
            <div className="fleet-panel__footer-actions">
              <button
                className={`fleet-panel__action-btn ${isTracking ? 'fleet-panel__action-btn--active' : ''}`}
                onClick={() => setIsTracking((prev) => !prev)}
              >
                <Navigation size={12} />
                {isTracking ? t('Siguiendo') : t('Seguir')}
              </button>
              <button
                className="fleet-panel__action-btn"
                onClick={handleLoadHistory}
                disabled={historyLoading}
              >
                {historyLoading ? <Skeleton className="fleet-panel__btn-spin" /> : <History size={12} />}
                {t('Recorrido')}
              </button>
              {historyPoints.length > 0 && !isReplaying && (
                <button className="fleet-panel__action-btn fleet-panel__action-btn--purple" onClick={handleStartReplay}>
                  {t('Replay')}
                </button>
              )}
              {isReplaying && (
                <button className="fleet-panel__action-btn fleet-panel__action-btn--warn" onClick={handleStopReplay}>
                  {t('Detener')}
                </button>
              )}
              <button
                className="fleet-panel__action-btn"
                onClick={fetchPendingPoints}
                disabled={pendingLoading}
              >
                {pendingLoading ? <Skeleton className="fleet-panel__btn-spin" /> : null}
                {t('Pendientes')}
              </button>
              {pendingPoints.length > 0 && (
                <button
                  className="fleet-panel__action-btn"
                  onClick={() => setShowPending((v) => !v)}
                >
                  {showPending ? t('Ocultar') : t('Mostrar')} ({pendingPoints.length})
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Loading overlay ── */}
      {isInitialLoading && snapshotLocations.size === 0 && (
        <div className="fleet-overlay fleet-overlay--loading">
          <div>
            <Skeleton className="fleet-overlay__skel" />
            <Skeleton className="fleet-overlay__skel" />
          </div>
          <p>{t('Cargando ubicaciones...')}</p>
        </div>
      )}

      {/* ── Replay progress bar ── */}
      {isReplaying && historyPoints.length > 0 && (
        <div className="fleet-replay-bar">
          <div className="fleet-replay-bar__info">
            <span>{t('Reproduciendo recorrido')}</span>
            <span>{replayIndex + 1} / {historyPoints.length}</span>
          </div>
          <div className="fleet-replay-bar__track">
            <div
              className="fleet-replay-bar__fill"
              style={{ width: `${((replayIndex + 1) / historyPoints.length) * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* ── WS desconectado ── */}
      {!isConnected && !isInitialLoading && (
        <div className="fleet-overlay fleet-overlay--warn">
          <WifiOff size={16} />
          <span>{t('Sin conexion en vivo — mostrando ultima ubicacion conocida')}</span>
        </div>
      )}
    </div>
  );
}
